/**
 * Server-sent events as the runner writes them, and as an application
 * relays them to a browser: `data:` lines carry one JSON document per
 * frame, `event:` names and comments carry nothing extra, keepalives are
 * `data: {}`.
 */
import { isObject } from './events.js';
/**
 * Build a parser that calls `onEvent` for every JSON object frame. Keepalive
 * frames (empty objects) and non-JSON data are dropped; a stream that ends
 * mid-frame delivers that frame from `finish`.
 */
export function createSseParser(onEvent) {
    let buffer = '';
    let data = [];
    const dispatch = () => {
        if (data.length === 0)
            return;
        const raw = data.join('\n');
        data = [];
        let parsed;
        try {
            parsed = JSON.parse(raw);
        }
        catch {
            return;
        }
        if (isObject(parsed) && Object.keys(parsed).length > 0)
            onEvent(parsed);
    };
    const line = (text) => {
        const clean = text.endsWith('\r') ? text.slice(0, -1) : text;
        if (clean === '') {
            dispatch();
            return;
        }
        if (clean.startsWith('data:')) {
            const payload = clean.slice(5);
            data.push(payload.startsWith(' ') ? payload.slice(1) : payload);
        }
    };
    return {
        push(chunk) {
            buffer += chunk;
            let index = buffer.indexOf('\n');
            while (index >= 0) {
                line(buffer.slice(0, index));
                buffer = buffer.slice(index + 1);
                index = buffer.indexOf('\n');
            }
        },
        finish() {
            if (buffer) {
                line(buffer);
                buffer = '';
            }
            dispatch();
        },
    };
}
/**
 * Read a fetch body to the end, delivering each event. Resolves when the
 * stream closes; rejects when the network drops it. The caller decides what
 * a close without `end` means (see `hasUnfinishedTurn`).
 */
export async function readEventStream(body, onEvent, signal) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    const parser = createSseParser(onEvent);
    const abort = () => {
        void reader.cancel().catch(() => undefined);
    };
    signal?.addEventListener('abort', abort, { once: true });
    try {
        for (;;) {
            const { value, done } = await reader.read();
            if (done)
                break;
            parser.push(decoder.decode(value, { stream: true }));
        }
        parser.push(decoder.decode());
        parser.finish();
    }
    finally {
        signal?.removeEventListener('abort', abort);
        reader.releaseLock();
    }
}
/** One frame in the runner's own framing, for a relay route. */
export function sseFrame(event) {
    const name = typeof event.type === 'string' && event.type ? event.type : 'message';
    return `event: ${name}\ndata: ${JSON.stringify(event)}\n\n`;
}
export const SSE_KEEPALIVE = 'event: keepalive\ndata: {}\n\n';
